import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from 'date-fns';
import { supabase } from '@/api/supabaseClient';
import { calculateInstallTime } from '@/utils/installTimeCalculator';
import { Calendar, Clock, User } from 'lucide-react';

export default function ScheduleInstallDialog({ date, installer, onClose }) {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [startTime, setStartTime] = useState('8:00 AM');
  const [estimatedHours, setEstimatedHours] = useState(0);
  const [loading, setLoading] = useState(false);

  const timeOptions = [
    '6:00 AM', '7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM',
    '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'
  ];

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (selectedProjectId) {
      const project = projects.find(p => p.id === selectedProjectId);
      setSelectedProject(project);
      if (project) {
        const hours = calculateInstallTime(project);
        setEstimatedHours(hours);
      }
    }
  }, [selectedProjectId, projects]);

  const loadProjects = async () => {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error loading projects:', error);
      return;
    }
    
    setProjects(data || []);
  };

  const handleSchedule = async () => {
    if (!selectedProjectId) {
      alert('Please select a project');
      return;
    }

    setLoading(true);

    const { error } = await supabase
      .from('scheduled_installs')
      .insert([{
        project_id: selectedProjectId,
        installer_id: installer.id,
        install_date: format(date, 'yyyy-MM-dd'),
        start_time: startTime,
        estimated_hours: estimatedHours,
        status: 'scheduled'
      }]);

    if (error) {
      console.error('Error scheduling install:', error);
      alert('Failed to schedule install');
      setLoading(false);
      return;
    }

    onClose();
  };

  const handleBlockTime = async () => {
    const reason = prompt('Reason for blocking this time (optional):');
    
    const { error } = await supabase
      .from('blocked_times')
      .insert([{
        installer_id: installer.id,
        block_date: format(date, 'yyyy-MM-dd'),
        start_time: startTime,
        end_time: '5:00 PM', // Default end time
        reason: reason || null
      }]);

    if (error) {
      console.error('Error blocking time:', error);
      alert('Failed to block time');
      return;
    }

    onClose();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Schedule Installation</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-2 p-3 bg-slate-50 rounded">
            <Calendar className="w-4 h-4 text-slate-600" />
            <span className="font-medium">{format(date, 'EEEE, MMMM d, yyyy')}</span>
          </div>

          <div className="flex items-center gap-2 p-3 bg-slate-50 rounded">
            <User className="w-4 h-4 text-slate-600" />
            <div
              className="w-3 h-3 rounded-full mr-1"
              style={{ backgroundColor: installer.color }}
            />
            <span className="font-medium">{installer.name}</span>
          </div>

          <div>
            <Label>Project</Label>
            <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map(project => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name || 'Untitled Project'}
                    {project.customer_name && ` - ${project.customer_name}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Start Time</Label>
            <Select value={startTime} onValueChange={setStartTime}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timeOptions.map(time => (
                  <SelectItem key={time} value={time}>
                    {time}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedProject && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 rounded border border-blue-200">
              <Clock className="w-4 h-4 text-blue-600" />
              <span className="text-sm">
                Estimated Duration: <span className="font-semibold">{estimatedHours} hours</span>
              </span>
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button 
              onClick={handleSchedule} 
              disabled={!selectedProjectId || loading}
              className="flex-1"
            >
              Schedule Install
            </Button>
            <Button 
              variant="outline" 
              onClick={handleBlockTime}
            >
              Block Time
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
